import { SessionHoldPanel } from '@/components/charging/SessionHoldPanel';
import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useStreamAwareInterval } from '@/store/sessionStream';
import { useTranslation } from 'react-i18next';
import { idleText } from '@evi/shared';
import { CalendarRange, Loader2, Star } from 'lucide-react';
import NamedIcon from '@/components/ui/NamedIcon';
import { awardReviewPoints, fetchSession } from '@/lib/api';
import { formatMoney, formatMoneyRate } from '@/lib/money';
import { useHomeSession } from '@/hooks/useHomeSession';
import { sessionFeeLine } from '@/lib/sessionFee';
import { IdlePanel } from '@/components/charging/IdlePanel';
import { StickyCTA, TokenSpin } from '@/components/primitives';
import styles from './SessionCompletePage.module.css';

interface Props { sessionId: string; onNavigate: (path: string) => void }

// Demo values when sessionId === 'demo' (no DB record). Card
// metadata stays here — the demo route is intentional fake
// context (URL signals it), so the marketing-screenshot
// "Charged to Visa •••• 4821" line holds. Card metadata was
// removed from the real-session path: /sessions/:id doesn't
// surface card brand / last4, and the previous fallback hardcoded
// "Charged to Visa ••••" on every real session-end regardless
// of which card actually ran. Polish ticket filed for adding
// stripe_card_brand + stripe_card_last4 to the Session payload
// (or reading from PaymentIntent on demand); until then the
// real-session footer reads vague-but-honest copy below.
const DEMO = {
  energy_kwh: 52.0,
  duration_minutes: 28,
  peak_kw: 182,
  rate_cents_per_kwh: 42,
  // B.2 — demo path always uses per_kwh display; this field exists
  // only to keep the union shape consistent with the real-session
  // branch. Read only when rateType==='per_hour', which never
  // happens for the demo session.
  rate_cents_per_hour: 0,
  total_cents: 2184,
};

export function SessionCompletePage({ sessionId, onNavigate }: Props) {
  const { t, i18n } = useTranslation('sessions');
  const isDemo = sessionId === 'demo';
  // Poll-until-completed (Ticket 2, C2). The stop flow navigates here
  // while the charger's StopTransaction echo is still finalizing, so
  // status='completed' and the weekly-streak stamp usually land AFTER
  // the first fetch. The page already tolerates still-finalizing data
  // (skeleton + interim values), so poll every 2s until the session
  // reads completed, capped at ~30s so a session that never finalizes
  // does not poll forever.
  // Live cards (28 Oct 2026): with the stream live, the session's own
  // events deliver the completion and the 2s poll is not needed.
  const pollStartRef = useRef(Date.now());
  const finalizePollMs = useStreamAwareInterval(2000);
  const sessionQ = useQuery({
    queryKey: ['session', sessionId],
    queryFn: () => fetchSession(sessionId),
    enabled: !isDemo,
    refetchInterval: (query) => {
      if (query.state.data?.idle_view?.cable_connected || query.state.data?.idle_view?.settlement_pending) return 5000;
      if (query.state.data?.status === 'completed') return false;
      if (Date.now() - pollStartRef.current > 30_000) return false;
      return finalizePollMs;
    },
  });

  const s = sessionQ.data;

  // Home-session framing: EVI never bills home charging, so the total
  // row carries the utility ESTIMATE (their entered rate) and the
  // footer drops the card-on-file line. Hook runs unconditionally
  // (before the skeleton return below); demo sessions never match.
  const home = useHomeSession(isDemo ? null : s);
  // The session's own currency (item 3): CA$ at a Canadian home. The
  // utility estimate on a home session is in the home's currency.
  const formatCents = (cents: number): string => formatMoney(cents, s?.cost?.currency, i18n.language);
  const formatHomeCents = (cents: number): string => formatMoney(cents, home.currency ?? s?.cost?.currency, i18n.language);

  // One-shot confetti latch: flips true the first time the payload
  // says THIS session advanced the weekly streak, and never back, so
  // re-renders and later refetches cannot re-fire the burst. The
  // mounted StreakConfetti's animation runs forwards once and ends
  // off-screen; keeping it mounted is inert.
  const [celebrate, setCelebrate] = useState(false);
  useEffect(() => {
    if (!isDemo && s?.weekly_streak_advanced) setCelebrate(true);
  }, [isDemo, s?.weekly_streak_advanced]);

  // Three render modes, mirroring the ChargingPage pattern from
  // commit 5cd617b:
  //   1. isDemo → DEMO synthetic values, /sessions/demo/complete
  //      route only (marketing-screenshot fidelity).
  //   2. !isDemo && !s → skeleton placeholder (NEW). Previously
  //      this case fell through to DEMO during the ~50-200ms
  //      sessionQ.data fetch window — a real driver finishing
  //      a real session saw a brief flash of "52 kWh, $21.84"
  //      before the truth landed. Skeleton keeps the layout
  //      stable while honest about the "we don't have the
  //      numbers yet" state.
  //   3. !isDemo && s → real values from /sessions/:id payload.
  if (!isDemo && !s) {
    return <SessionCompleteSkeleton onNavigate={onNavigate} />;
  }

  // B.2 — derive both possible rate units. Which one we display
  // is chosen by `s.tariff_rate_type` (surfaced by GET /sessions/:id
  // from a parallel TariffModel projection). Per-kWh sessions show
  // `$X.XX / kWh` (the historical row); per-hour sessions show
  // `$X.XX / hour` derived from cost/duration so the unit on the
  // row matches the billing model. Defaults to per_kwh when the
  // field is absent (legacy sessions without a tariff_id).
  const rateType: 'per_kwh' | 'per_hour' = !isDemo && s
    ? (s.tariff_rate_type ?? 'per_kwh')
    : 'per_kwh';
  const data = isDemo || !s ? DEMO : {
    energy_kwh: s.energy_kwh,
    duration_minutes: s.duration_minutes,
    peak_kw: Math.max(0, ...(s.meter_values ?? []).map((mv) => mv.power_kw)),
    rate_cents_per_kwh: s.energy_kwh > 0 ? Math.round((s.cost?.energy_cost ?? 0) / s.energy_kwh) : 0,
    // Per-hour: derive cents/hour from cost/duration. duration is in
    // minutes; convert to hours. Zero-duration sessions (defensive
    // guard) → 0 to avoid Infinity.
    rate_cents_per_hour: s.duration_minutes > 0
      ? Math.round((s.cost?.energy_cost ?? 0) / (s.duration_minutes / 60))
      : 0,
    total_cents: s.cost?.total ?? 0,
  };
  // Fee and tax rows (real sessions only; the demo has neither).
  const feeLine = !isDemo && s ? sessionFeeLine(s) : null;
  const idle = !home.isHomeSession ? s?.idle_view : null;
  const idleOpen = !!idle && !idle.cable_removed_at;
  const taxCents = Math.round(idleOpen ? idle!.charging_tax_cents : idle?.tax_cents ?? s?.cost?.tax ?? 0);

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <div className={styles.checkWrap}>
          <div aria-hidden className={styles.checkHalo} />
          {/*
            The accent-coloured circle is now a frame around the
            spinning EVI token. A thin ring of the circle stays
            visible at the coin's edge — see `.checkCircle` CSS for
            the ~5px rim math. Token reuses the existing
            reduced-motion-aware WebP pair already on Profile +
            Rewards.
          */}
          <div className={styles.checkCircle}>
            <TokenSpin size={78} title={t('token.complete')} />
          </div>
        </div>

        <p className={styles.eyebrow}>{t('eyebrow')}</p>
        <h1 className={styles.title}>{idleOpen ? idleText(i18n.language, 'chargingEnded') : t('title')}</h1>
        {!home.isHomeSession && <IdlePanel session={s} onNavigate={onNavigate} />}

      <SessionHoldPanel session={s} />
        <div className={styles.statsCard}>
          <StatRow label={t('stats.energyAdded')} value={data.energy_kwh.toFixed(2)} suffix="kWh" />
          <StatRow label={t('stats.duration')} value={formatDuration(data.duration_minutes)} />
          <StatRow label={t('stats.peakPower')} value={String(Math.round(data.peak_kw))} suffix="kW" />
          {/* Home sessions hide the $0.00 EVI rate row — the number
              that matters is the utility estimate in the total row. */}
          {!home.isHomeSession && (
            <StatRow
              label={t('stats.rate')}
              value={rateType === 'per_hour'
                ? formatCents(data.rate_cents_per_hour)
                : formatCents(data.rate_cents_per_kwh)}
              suffix={rateType === 'per_hour' ? t('stats.perHour') : '/ kWh'}
            />
          )}
          {/* Idle fee (tariff time_rate x idle minutes, billed into
              cost.time_cost since 2026-09-27). Without this row the total
              did not reconcile with rate x energy: the driver saw a bigger
              number and no reason for it. The Session carries only the
              amount (idle minutes are computed at StopTransaction and not
              persisted), so the row shows the amount alone. Not on a home
              session: its total row is a utility estimate, not a bill. */}
          {!isDemo && !home.isHomeSession && !idleOpen && (idle?.idle_cents ?? s?.cost?.time_cost ?? 0) > 0 && (
            <StatRow
              label={t('stats.idleFee')}
              value={formatCents(Math.round(idle?.idle_cents ?? s?.cost?.time_cost ?? 0))}
            />
          )}
          {/* The fixed-fee and tax lines, so energy + idle + fee + tax
              adds up to the total below. The fee line is sessionFeeLine,
              the same helper (and the same labels) as SessionDetailPage
              and the receipt PDF: EVI's connection fee on a session that
              pinned it at start, the tariff's session fee on a legacy one.
              Tax only when there is some. Not on a home session, whose
              total row is a utility estimate, not a bill. */}
          {!isDemo && !home.isHomeSession && feeLine && (
            <StatRow
              label={feeLine.kind === 'connection' ? t('detail.break.connectionFee') : t('detail.break.sessionFee')}
              value={formatCents(feeLine.cents)}
            />
          )}
          {!isDemo && !home.isHomeSession && taxCents > 0 && (
            <StatRow label={t('detail.break.tax')} value={formatCents(taxCents)} />
          )}
          <div className={styles.divider} />
          <div className={styles.totalRow}>
            <span className={styles.totalLabel}>
              {home.isHomeSession && home.estimateCents != null ? t('totalLabel.estUtilityCost')
                : idleOpen ? idleText(i18n.language, 'chargingTotal') : t('totalLabel.total')}
            </span>
            <span className={styles.totalValue}>
              {home.isHomeSession
                ? (home.estimateCents != null ? formatHomeCents(Math.round(home.estimateCents)) : t('free'))
                : formatCents(idleOpen ? idle!.charging_total_cents : idle?.total_cents ?? data.total_cents)}
            </span>
          </div>
          {/* Fleet billing indicator (Phase 1 fleet platform). Renders
              only on sessions that were billed to a Fleet. Inline-styled
              for consistency with the PreSessionPage pill. */}
          {!isDemo && s?.billed_to === 'fleet' && s.fleet_name && (
            <div
              style={{
                marginTop: 10,
                padding: '6px 10px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'color-mix(in srgb, var(--evi-accent, #2ECC71) 12%, transparent)',
                border: '1px solid color-mix(in srgb, var(--evi-accent, #2ECC71) 35%, transparent)',
                borderRadius: 999,
                fontSize: 11,
                color: 'var(--color-tx)',
              }}
            >
              {t('fleet.billedTo')} <strong style={{ marginLeft: 4 }}>{s.fleet_name}</strong>
            </div>
          )}
        </div>

        {/* Phase 5 multi-window TOU breakdown. Renders below the
            stats card when the session crossed window boundaries
            (cost_segments.length > 1). Single-segment sessions
            keep the stats-only display above. Demo sessions skip
            this entirely — DEMO has no cost_segments. */}
        {!isDemo && s?.cost_segments && s.cost_segments.length > 1 && (
          <SegmentBreakdown segments={s.cost_segments} currency={s.cost?.currency} />
        )}

        {/* Weekly streak moment (Ticket 2, C2). Renders only when THIS
            session advanced the streak to a new ISO week, so it shows
            at most once a week. Week 1 covers both a brand-new streak
            and a comeback after a gap, the intended onboarding flavor.
            The banner stays visible under reduced motion; only the
            confetti hides (its layer is display:none there). */}
        {!isDemo && s?.weekly_streak_advanced && (
          <div className={styles.streakBanner} role="status">
            <span className={styles.streakIcon} aria-hidden>
              <CalendarRange size={16} />
            </span>
            <span className={styles.streakText}>
              {(s.weekly_streak_weeks ?? 1) <= 1
                ? <>{t('streak.started')} <strong>{t('streak.week', { n: 1 })}</strong></>
                : <>{t('streak.alive')} <strong>{t('streak.week', { n: s.weekly_streak_weeks })}</strong></>}
              {(s.weekly_streak_milestone_points ?? 0) > 0 && (
                <span className={styles.streakBonus}>
                  {t('streak.bonusPoints', { points: s.weekly_streak_milestone_points })}
                </span>
              )}
            </span>
          </div>
        )}

        {/* Phase 3 of gamification rules update — Google review CTA.
            Renders only when the session's location has a configured
            google_place_id (server returns review_award_state). The
            CTA uses the state passed by the server to decide whether
            to render enabled, disabled-because-already-awarded, or
            not at all. Demo sessions always show the CTA in the
            "available" mode for marketing-screenshot fidelity. */}
        {!isDemo && s && s.review_award_state && s.review_award_state !== 'unavailable' && s.google_place_id && (
          <ReviewCta
            sessionId={s._id}
            placeId={s.google_place_id}
            initialState={s.review_award_state}
            redirectUrl={s.review_redirect_url}
          />
        )}

        {/* Footer note — demo route keeps the specific "Visa
            •••• 4821" line for marketing-screenshot fidelity (URL
            signals fake context). Real sessions get vague-but-
            honest copy: the /sessions/:id payload doesn't yet
            surface card brand / last4, and faking "Visa ••••"
            universally was visible deception (drivers paying
            with Amex still saw "Visa"). */}
        <p className={styles.footerNote}>
          {isDemo
            ? t('footer.chargedTo', { card: 'Visa •••• 4821' })
            : home.isHomeSession
              ? (home.estimateCents != null
                ? t('footer.homeEstimated', { rate: home.rateSummary })
                : t('footer.homeNotBilled'))
              : idle ? (idleOpen && !idle.billing_closed ? idleText(i18n.language, 'pendingNote') : '') : t('footer.chargedToCardOnFile')}
        </p>
      </div>

      {/* Fired once via the celebrate latch above; the layer is
          fixed, pointer-events:none, and hidden entirely under
          prefers-reduced-motion. */}
      {celebrate && <StreakConfetti />}

      <StickyCTA sticky={false} onClick={() => onNavigate('/map')}>{t('done')}</StickyCTA>
    </div>
  );
}

/**
 * CSS-only confetti shower for the weekly-streak moment. Copied from
 * RedemptionSheet's Confetti (the established pattern) rather than
 * imported, so the redemption flow stays untouched; the CSS classes
 * live in this page's module with the same prefers-reduced-motion
 * guard. 40 particles, randomized drift / rotation / duration /
 * delay via CSS custom properties, no JS animation loop, no new
 * dependency.
 */
const CONFETTI_COLORS = ['#FACC15', '#22C55E', '#FB923C', '#60A5FA', '#F472B6'];
const CONFETTI_COUNT = 40;

function StreakConfetti(): JSX.Element {
  // Particles are deterministic per mount via useMemo so React's
  // strict-mode double-render in dev doesn't double-randomize and
  // visibly stutter. The celebrate latch mounts this exactly once
  // per page view.
  const particles = useMemo(() => {
    return Array.from({ length: CONFETTI_COUNT }, (_, i) => {
      const left = Math.random() * 100;             // start x in vw
      const xDrift = (Math.random() - 0.5) * 40;    // -20 to +20 vw drift
      const rot = (Math.random() - 0.5) * 1080;     // -540 to +540 deg
      const dur = 2 + Math.random() * 1.2;          // 2 to 3.2s
      const delay = Math.random() * 0.6;            // 0 to 0.6s stagger
      const bg = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
      return { left, xDrift, rot, dur, delay, bg, key: i };
    });
  }, []);

  return (
    <div className={styles.confettiLayer} aria-hidden>
      {particles.map((p) => (
        <span
          key={p.key}
          className={styles.confettiPiece}
          style={{
            // CSS custom properties consumed by the keyframe.
            // Cast through Record so React accepts the --prefixed
            // keys without complaining about CSSProperties typing.
            ['--left' as string]: String(p.left),
            ['--x' as string]: String(p.xDrift),
            ['--rot' as string]: `${p.rot}deg`,
            ['--dur' as string]: `${p.dur}s`,
            ['--delay' as string]: `${p.delay}s`,
            ['--bg' as string]: p.bg,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Google review CTA — opens Google Maps' review-write surface for
 * the location's place_id and credits +5 points server-side.
 *
 * Trust-on-tap: the +5 award fires on click, before (and independent
 * of) whether the user actually submits a review on Google's side.
 * Locked decision per Phase 3 spec.
 *
 * Local state mirrors the server's three-state response shape from
 * /sessions/:id (`available` | `awarded`) plus a transient `pending`
 * during the in-flight POST. We deliberately don't optimistically
 * flip to `awarded` before the response — if the network fails, the
 * review link itself still opens (review action is useful regardless),
 * but the points portion can be retried via a re-tap rather than
 * stuck in a wrongly-disabled state.
 *
 * Order of operations on click:
 *   1. Open the review window FIRST. window.open after an async
 *      response is blocked by popup heuristics on Safari/iOS — the
 *      browser only honors window.open when it's still inside the
 *      synchronous handler from the user's gesture. The points
 *      request runs in parallel with the user's review action.
 *   2. Fire the points POST in the background. State updates from
 *      the response.
 */
type CtaState =
  | { kind: 'available' }
  | { kind: 'pending' }
  | { kind: 'awarded'; pointsAwarded: number }
  | { kind: 'error'; message: string };

function ReviewCta({
  sessionId, placeId, initialState, redirectUrl,
}: {
  sessionId: string;
  placeId: string;
  initialState: 'available' | 'awarded';
  /** Tracked review link minted by the server (Commit 2). When
   *  present, the tap opens it instead of the locally-built Google
   *  URL — the server logs the click, awards the +5 on the same
   *  idempotency key, and 302s onward. Absent on old servers, where
   *  the tap behaves exactly as before. */
  redirectUrl?: string;
}) {
  const { t } = useTranslation('sessions');
  const [state, setState] = useState<CtaState>(
    initialState === 'awarded'
      ? { kind: 'awarded', pointsAwarded: 5 }
      : { kind: 'available' },
  );

  const reviewUrl = `https://search.google.com/local/writereview?placeid=${encodeURIComponent(placeId)}`;

  const handleClick = (): void => {
    if (state.kind === 'awarded' || state.kind === 'pending') return;

    // Open the review window in the same tick as the click so iOS
    // Safari's popup heuristic accepts it as user-initiated. We
    // don't await the points call before opening — the review action
    // is useful regardless of whether the points credit lands. The
    // tracked redirect (when minted) is preferred; both URLs are
    // already in hand, so the open stays synchronous either way.
    window.open(redirectUrl ?? reviewUrl, '_blank', 'noopener,noreferrer');

    setState({ kind: 'pending' });
    // Belt-and-suspenders for one release: the redirect endpoint
    // already awards on the same session_review:<sessionId> key, so
    // this POST covers a blocked popup or an expired token and can
    // never double-pay — whichever path lands second collapses to an
    // idempotent no-op. A later cleanup removes it once every client
    // routes through the redirect.
    void awardReviewPoints(sessionId)
      .then((result) => {
        setState({ kind: 'awarded', pointsAwarded: result.points });
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Failed to award points';
        setState({ kind: 'error', message: msg });
      });
  };

  const disabled = state.kind === 'awarded' || state.kind === 'pending';
  const label = (() => {
    switch (state.kind) {
      case 'awarded':
        return t('review.awarded', { points: state.pointsAwarded });
      case 'pending':
        return t('review.awarding');
      case 'error':
        return t('review.retry');
      case 'available':
      default:
        return t('review.leave');
    }
  })();
  const ariaLabel = state.kind === 'awarded'
    ? t('review.ariaAwarded', { points: state.pointsAwarded })
    : t('review.ariaLeave');

  return (
    <button
      type="button"
      className={styles.reviewCta}
      onClick={handleClick}
      disabled={disabled}
      aria-label={ariaLabel}
      data-state={state.kind}
    >
      {/* Celebratory one-shot — loop={false} overrides the default
          loop policy. The burst plays once when the CTA enters view
          and again on tap. */}
      <NamedIcon
        name="session-complete-burst"
        fallback={<Star size={16} className={styles.reviewIcon} aria-hidden />}
        size={16}
        loop={false}
      />
      <span className={styles.reviewLabel}>{label}</span>
    </button>
  );
}

/**
 * Loading skeleton for the real-session fetch window. Mirrors
 * the loaded layout — token spinner (continues animating, doubles
 * as a loading indicator), eyebrow + title placeholders, four
 * stat-row placeholders, total row, footer note. Keeps the page
 * height stable so the celebration framing doesn't shift when the
 * real data lands. Inline rather than extracted because nothing
 * else needs this shape.
 */
function SessionCompleteSkeleton({ onNavigate }: { onNavigate: (path: string) => void }) {
  const { t } = useTranslation('sessions');
  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <div className={styles.checkWrap}>
          <div aria-hidden className={styles.checkHalo} />
          <div className={styles.checkCircle}>
            <TokenSpin size={78} title={t('token.loading')} />
          </div>
        </div>

        <p className={styles.eyebrow}>
          <Loader2 size={12} className={styles.skeletonSpin} /> {t('skeleton.loading')}
        </p>
        <h1 className={styles.title}>{t('skeleton.oneMoment')}</h1>

        <div className={styles.statsCard}>
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
          <div className={styles.divider} />
          <div className={styles.totalRow}>
            <span className={styles.totalLabel}>{t('totalLabel.total')}</span>
            <span className={`${styles.totalValue} ${styles.skeletonShimmer}`} aria-hidden>
              ——
            </span>
          </div>
        </div>

        <p className={styles.footerNote}>&nbsp;</p>
      </div>

      <StickyCTA sticky={false} onClick={() => onNavigate('/map')}>{t('done')}</StickyCTA>
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className={styles.statRow}>
      <span className={`${styles.statLabel} ${styles.skeletonShimmer}`}>————</span>
      <span className={`${styles.statValue} ${styles.skeletonShimmer}`}>——</span>
    </div>
  );
}

function StatRow({ label, value, suffix }: { label: string; value: string; suffix?: string }) {
  return (
    <div className={styles.statRow}>
      <span className={styles.statLabel}>{label}</span>
      <strong className={styles.statValue}>
        {value}
        {suffix && <span className={styles.statSuffix}>{suffix}</span>}
      </strong>
    </div>
  );
}

function formatDuration(min: number): string {
  const total = Math.max(0, Math.round(min));
  const h = Math.floor(total / 60);
  const m = total % 60;
  const s = 0;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// ── Phase 5 segment breakdown ──────────────────────────────────────
//
// Renders the per-window cost breakdown for sessions that crossed
// TOU window boundaries. Reads cost_segments off SessionDetail —
// the field is populated by StopTransaction's segmentSession call
// (commit 7b46a1a) and surfaced verbatim by GET /sessions/:id.

interface SegmentRow {
  start_at: string;
  end_at: string;
  duration_minutes: number;
  energy_kwh: number;
  rate_cents: number;
  rate_type: 'per_kwh' | 'per_hour';
  window_label: string;
  cost_cents: number;
}

function SegmentBreakdown({ segments, currency }: { segments: SegmentRow[]; currency?: string }): JSX.Element {
  const { t, i18n } = useTranslation('sessions');
  // Every segment is in the session's currency (item 3).
  const formatCents = (cents: number): string => formatMoney(cents, currency, i18n.language);
  // A segment's rate keeps a fraction of a cent: a flat fallback segment carries the location's 43.63, "$0.4363".
  const formatRate = (cents: number): string => formatMoneyRate(cents, currency, i18n.language);
  return (
    <div className={styles.breakdownCard}>
      <p className={styles.breakdownTitle}>{t('breakdown.title')}</p>
      {segments.map((seg, i) => (
        <div key={i} className={styles.breakdownRow}>
          <div className={styles.breakdownRowTop}>
            <span className={styles.breakdownRange}>
              {formatClockRange(seg.start_at, seg.end_at)}
            </span>
            <span className={styles.breakdownCost}>
              {formatCents(seg.cost_cents)}
            </span>
          </div>
          <div className={styles.breakdownRowMeta}>
            <span className={styles.breakdownLabel}>{seg.window_label}</span>
            ·{' '}{formatSegmentUsage(seg, formatRate)}
          </div>
        </div>
      ))}
    </div>
  );
}

function formatClockRange(startISO: string, endISO: string): string {
  return `${formatClock(new Date(startISO))} – ${formatClock(new Date(endISO))}`;
}

function formatClock(d: Date): string {
  // 12-hour clock matching the PWA's existing convention (see
  // apps/web/src/lib/time-format.ts:formatHour12). Inlined here to
  // avoid pulling in the helper for a single render path.
  const h = d.getHours();
  const m = d.getMinutes();
  const period = h < 12 ? 'AM' : 'PM';
  const display = h % 12 === 0 ? 12 : h % 12;
  return `${display}:${String(m).padStart(2, '0')} ${period}`;
}

function formatSegmentUsage(seg: SegmentRow, formatCents: (cents: number) => string): string {
  // per_kwh: "1.7 kWh × $0.18/kWh"
  // per_hour: "30 min × $1.50/hour"
  const rateDollars = formatCents(seg.rate_cents);
  if (seg.rate_type === 'per_hour') {
    return `${seg.duration_minutes} min × ${rateDollars}/hour`;
  }
  return `${seg.energy_kwh.toFixed(1)} kWh × ${rateDollars}/kWh`;
}
